import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.addEventListener('click', this.#onBackdropClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onBackdropClick);
  }

  /**
   * Handle backdrop clicks to close drawer, but not if clicking within mega-menu-section
   * @param {MouseEvent} event
   */
  #onBackdropClick = (event) => {
    const backdrop = this.querySelector('.menu-drawer__backdrop');
    if (!backdrop) return;

    // Only close if clicking on the backdrop itself, not if click bubbled from within the drawer or mega-menu-section
    if (event.target !== backdrop) return;

    const megaMenuSection = document.querySelector('.mega-menu-section');
    if (megaMenuSection && megaMenuSection.contains(event.target)) return;

    this.close();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    const megaMenuSection = document.querySelector('.mega-menu-section');
    if (megaMenuSection) {
      megaMenuSection.querySelectorAll('.mega-menu__screen').forEach((screen) => {
        screen.classList.remove('is-visible');
        screen.setAttribute('aria-expanded', 'false');
      });
      megaMenuSection.querySelectorAll('.mega-menu__submenu-column').forEach((col) => {
        col.classList.remove('is-visible');
      });
    }

    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      megaMenuSection?.classList.add('is-mobile');
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        megaMenuSection?.classList.add('menu-drawer--open');
      });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');
    const megaMenuSection = document.querySelector('.mega-menu-section');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    let closeCompleteFired = false;
    const onCloseComplete = () => {
      if (closeCompleteFired) return;
      closeCompleteFired = true;
      if (megaMenuSection) {
        megaMenuSection.classList.add('menu-drawer--closed');
        requestAnimationFrame(() => {
          megaMenuSection.classList.remove(
            'is-mobile',
            'menu-drawer--open',
            'menu-drawer--closing',
            'menu-drawer--closed'
          );
        });
      }
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    };

    if (megaMenuSection?.classList.contains('menu-drawer--open')) {
      megaMenuSection.classList.add('menu-drawer--closing');
      const durationStr = getComputedStyle(megaMenuSection).transitionDuration;
      const durationMs = durationStr.endsWith('s')
        ? parseFloat(durationStr) * 1000
        : parseFloat(durationStr);
      const delay = Number.isFinite(durationMs) ? durationMs + 20 : 300;
      const timeoutId = setTimeout(onCloseComplete, delay);
      const handleTransitionEnd = (/** @type {TransitionEvent} */ event) => {
        if (event.target !== megaMenuSection || event.propertyName !== 'transform') return;
        megaMenuSection.removeEventListener('transitionend', handleTransitionEnd);
        clearTimeout(timeoutId);
        onCloseComplete();
      };
      megaMenuSection.addEventListener('transitionend', handleTransitionEnd);
    } else {
      onAnimationEnd(details, onCloseComplete);
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
